import { sessionState } from '../../../auth/session-state';
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../../auth/auth.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import * as myGlobalsfunc from '../../../global.function';
import { MfaChallengeService } from '../../../mfa/mfa-challenge.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-relogin',
    templateUrl: './relogin.component.html',
    styleUrls: ['./relogin.component.scss'],
    standalone: false
})
export class ReloginComponent implements OnInit {
  @Input() displayRelogin: Boolean = false;
  @Input() displayReloginMfa: Boolean = false;
  public password: any;
  public mfaCode: any;
  reloginForm: UntypedFormGroup;
  reloginMfaForm: UntypedFormGroup;
  submitted = false;
  public msgs = [];
  public checkSessionRequestInterval;
  public limitResend: Boolean = false;
  private numberReg: RegExp;

  constructor(
    private authService: AuthService,
    private ngxSpinnerService: NgxSpinnerService,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private mfaChallengeService: MfaChallengeService,
    private router: Router,
  ) {
    translate.setDefaultLang('en');
  }

  useLanguage(language: string) {
    this.translate.use(language);
  }

  // Return the form controls
  get f() {
    return this.reloginForm.controls;
  }

  get m() {
    return this.reloginMfaForm.controls;
  }

  // fn: show error
  showError(type, message) {
    this.msgs = [];
    this.msgs.push({ severity: type, summary: message });
  }

  codeLength(event) {
    if(this.reloginMfaForm.get('mfaCode').value.length >= 6 || !this.numberReg.test(event.key)) {
      event.preventDefault();
    }
  }

  ngOnInit() {
    this.numberReg = new RegExp('^[0-9]*$');
    this.reloginForm = this.formBuilder.group({
      password: [
        '',
        [
          Validators.required,
          Validators.maxLength(255),
          Validators.minLength(8)
        ]
      ]
    });
    this.reloginMfaForm = this.formBuilder.group({
      mfaCode: [
        '',
        [
          Validators.required,
          Validators.maxLength(6),
          Validators.minLength(6),
          Validators.pattern('^[0-9]*$')
        ]
      ]
    });
    this.authService.eventEmitter.subscribe(data => {
      if (this.checkSessionRequestInterval) {
        clearInterval(this.checkSessionRequestInterval);
      }
      this.ngxSpinnerService.hide();
      this.displayRelogin = true;
      sessionState.setItem('access_token', '');
      sessionState.setItem('expires_in', '');
      sessionState.setItem('id_token', '');
      sessionState.setItem('user_id', '');
    });

    this.authService.mfaEmitter.subscribe(data => {
      // Repeated expired requests must not resend mail or clear an entered code.
      if (this.displayReloginMfa) return;
      if(!sessionState.getItem('mfa_email_sent') || sessionState.getItem('mfa_email_sent') !== '1') {
        this.mfaChallengeService.resendCode({isMFAChallenge: true}).subscribe((res: any) => { });
        sessionState.setItem('mfa_email_sent', '1');
      }
      this.ngxSpinnerService.hide();
      this.mfaCode = '';
      this.submitted = false;
      this.displayReloginMfa = true;
    });
  }

  resendCode() {
    this.limitResend = true;
    this.msgs = [];
    this.ngxSpinnerService.show();

    setTimeout(() => {
      this.limitResend = false;
    }, 30000); 

    this.mfaChallengeService.resendCode({isMFAChallenge: true})
      .subscribe(
        (res: any) => {
          if(res.status === 1) {
            this.msgs.push({ severity: 'success', summary: 'Another MFA code has been emailed to you.', closable: false });
            this.ngxSpinnerService.hide();
          } else {
            this.showError('error', res.message);
            this.ngxSpinnerService.hide();
            return;
          }
        }
      );
  }

  backToLogin() {
    this.ngxSpinnerService.show();
    const userId = sessionState.getItem('user_id');
    const token = sessionState.getItem('id_token');

    if(userId !== '' && userId !== '0' && token !== '') {
      this.mfaChallengeService.clearSession({ user_id: userId, token: token })
        .subscribe(
          (res: any) => {
            sessionState.clear();
            this.router.navigate(['/login']);
            this.displayReloginMfa = false;
            this.ngxSpinnerService.hide();
            return;
          }
        );
    } else {
      sessionState.clear();
      this.ngxSpinnerService.hide();
      this.router.navigate(['/login']);
      return;
    }
  }

  // On successfull form validation invoke this function
  onSubmit() {
    this.submitted = true;
    this.ngxSpinnerService.show();
    this.msgs = [];
    // stop here if reloginForm is invalid
    if (this.reloginForm.invalid) {
      return;
    }

    const senddata: any = {};
    senddata.password = this.reloginForm.value.password;
    this.authService.relogin(senddata).subscribe(
      data => {
        this.ngxSpinnerService.hide();
        this.submitted = false;
        if (data.status === 1) {
          // Re-login sets fresh auth cookies on the response; do not overwrite them.

          // Restart check session request
          this.checkSessionRequestInterval = setInterval(() => {
            if (this.authService.isLoggedIn()) {
              const user_id: any = sessionState.getItem('user_id');
              this.authService.isValidUser(user_id).subscribe(data => {});
            }
          }, 300000);

          if(data.user.mfa_required && !data.user.mfa_passed) {
            sessionState.setItem('mfa_required', 1);
            sessionState.setItem('mfa_passed', 0);
            this.displayRelogin = false;
            this.displayReloginMfa = true;
            this.submitted = false;
            this.mfaCode = '';
          } else {
            this.displayRelogin = false;
          }
          return;
        } else {
          this.showError('error', data.message);
        }
      },
      error => {
        this.ngxSpinnerService.hide();
        this.submitted = false;
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            this.displayRelogin = false;
          } else {
            this.showError(
              'error',
              'Oops something went wrong. Please contact to your administrator.'
            );
          }
        }
      }
    );
  }

  onSubmitMfa() {
    if (this.reloginMfaForm.invalid) {
      return;
    }
    this.submitted = true;
    this.ngxSpinnerService.show();
    this.msgs = [];

    this.mfaChallengeService.challengeCode({ code: this.reloginMfaForm.get('mfaCode').value })
      .subscribe(
        (res: any) => {
          if (res.status === 1 && res.mfa_passed === 1) {
            // The verification response sets the MFA cookies, including expiry.
            window.location.reload();
            return;
          } else {
            window.scroll(0, 0);
            this.showError('error', res.message);
            this.ngxSpinnerService.hide();
            this.submitted = false;
            return;
          }
        }
      );
  }
}
